/**
 * เอกสารสั่งการระดับแอปจาก Firestore `app_control/{channel}`
 *
 * รวมทุกสวิตช์ของ channel ไว้ใน doc เดียว: reload / ประกาศ / stock_checkpoint
 * doc ใช้กับ "ทุกร้าน" บน channel นั้น ไม่มี target
 * สั่งเฉพาะร้านแยกไปอยู่ที่ `app_control_stores/{storeId}` (ดู AppControlStoreDoc)
 *
 * reload เทียบด้วย `reload.hash` ว่า "ต่างจากที่เคยเห็นไหม" ไม่เทียบเลขเวอร์ชัน
 * เพื่อให้สั่งโหลดซ้ำบนแอปเวอร์ชันเดิมได้ (เช่น ล้างแคชหลังแก้ข้อมูลฝั่ง server)
 * กันวนโหลดด้วยการจด hash ลง localStorage ก่อนสั่งโหลดเสมอ
 * ฟิลด์อื่นนอกจากที่ประกาศใน AppControlDoc ถ้ามีค้างใน doc แอปจะเมินทิ้งเฉย ๆ
 *
 * docId ตายตัว 4 ตัวตาม BUILD_TARGET ที่ใช้ build (ดู sentryRelease.mjs / useBuildChannel.ts)
 * ไม่ derive จาก hostname อีกแล้ว — ประกาศบน beta จะได้ไม่มีทางไปโผล่บน production
 *
 * เอกสารที่ต้องสร้างในคอนโซล + รูปแบบฟิลด์ ดูที่ plans/app-control.md
 * พฤติกรรม reload ฝั่งเครื่อง (รอเครื่องว่าง / สุ่มเวลา / toast) ดูที่ plans/app-reload.md
 */

export type BuildChannel = "production" | "beta" | "preview" | "development";

export const BUILD_CHANNELS: readonly BuildChannel[] = [
  "production",
  "beta",
  "preview",
  "development",
];

/**
 * docId ของแต่ละ channel — preset ตายตัว ห้ามสร้าง docId นอกลิสต์นี้
 *
 * เขียนเป็น map แทนที่จะใช้ชื่อ channel ตรง ๆ เพราะถ้าวันหนึ่งต้องเปลี่ยนชื่อ doc
 * (เช่นชนกับของโปรเจกต์อื่นใน Firestore เดียวกัน) จะได้แก้ที่เดียว
 */
export const APP_CONTROL_DOC_ID: Record<BuildChannel, string> = {
  production: "production",
  beta: "beta",
  preview: "preview",
  development: "development",
};

export const ANNOUNCEMENT_LEVELS = ["info", "warning", "error"] as const;

export type AnnouncementLevel = (typeof ANNOUNCEMENT_LEVELS)[number];

export interface AppControlReload {
  /** ค่าสุ่มอะไรก็ได้ เครื่องจะโหลดใหม่เมื่อค่านี้ "ต่าง" จากที่จดไว้ (ไม่ใช่มากกว่า) */
  hash: string;
  /** เหตุผลที่สั่งโหลดใหม่ ส่งขึ้น Sentry ด้วย จะได้ไล่ย้อนได้ว่ารอบนั้นสั่งเพราะอะไร */
  reason?: string;
  /** บรรทัดที่สองของ toast "ระบบมีการอัปเดตเรียบร้อยแล้ว" หลังโหลดเสร็จ ไม่ใส่ = ไม่มีบรรทัดที่สอง */
  message?: string;
  updatedAt?: string;
}

export interface AppControlAnnouncement {
  enabled: boolean;
  level: AnnouncementLevel;
  message_th?: string;
  message_en?: string;
  /** ISO string — ก่อนเวลานี้ยังไม่ต้องโชว์ */
  startAt?: string;
  /** ISO string — เลยเวลานี้แล้วเลิกโชว์ */
  endAt?: string;
  dismissible?: boolean;
  /** ใช้เป็นกุญแจของการกดปิด ประกาศใหม่ = updatedAt ใหม่ = กลับมาโชว์อีกครั้ง */
  updatedAt?: string;
}

export interface AppControlDoc {
  reload?: AppControlReload;
  announcement?: AppControlAnnouncement;
  /**
   * สวิตช์หน้าสต๊อก (ฟิลด์ระดับ root ของ doc ไม่ซ้อนใน object) — ไม่มีฟิลด์ = ปิด
   * true = คำนวณในเครื่อง (IndexedDB + checkpoint จาก /inventories-v2/sync)
   * false / ไม่มี / อ่าน Firestore ไม่ได้ = ทำงานแบบเดิม ให้ server คำนวณผ่าน /inventories-tx/*
   * เห็นค่าไม่เป็น true เมื่อไหร่ เครื่องลบแคชสต๊อกทุกร้าน (ดู plans/app-control.md)
   */
  stock_checkpoint?: boolean;
}

/**
 * เอกสารสั่งการเฉพาะร้าน Firestore `app_control_stores/{storeId}`
 *
 * แยก collection ออกมาจาก doc ร้าน `stores/{storeId}` เพราะ root doc ของร้านถูกเขียนถี่
 * (สถานะร้าน ฯลฯ) ถ้าเครื่องหน้าร้านฟัง root doc ทุกการเขียน = 1 read ต่อเครื่อง
 * doc นี้ถูกเขียนแค่ตอนแอดมินสั่งเท่านั้น และไม่ผูกกับ channel — ทุก build ของร้านนั้นฟังตัวเดียวกัน
 */
export interface AppControlStoreDoc {
  reload?: AppControlReload;
}

export const isBuildChannel = (value: unknown): value is BuildChannel =>
  typeof value === "string" && BUILD_CHANNELS.includes(value as BuildChannel);
